/**
 * Scene setup and the render loop (spec §9.5, §6.5).
 *
 * M0 renders the test terrain with a WebGL2 renderer. Spec §9.1 calls for
 * Three.js `WebGPURenderer` with an automatic WebGL2 fallback; that switch happens
 * in M1, together with the water surface and the overlay shader pass that actually
 * need the node materials. Using WebGL2 for M0 keeps CI screenshots (SwiftShader
 * on Linux) identical to local ones. See docs/DECISIONS.md ADR-0006.
 */

import {
  BufferAttribute,
  BufferGeometry,
  Color,
  DirectionalLight,
  Fog,
  HemisphereLight,
  Mesh,
  MeshStandardMaterial,
  PerspectiveCamera,
  Scene,
  WebGLRenderer,
} from 'three';
import type { Heightfield } from './terrain.js';

/** Colour ramp from valley floor to ridge. Perceptually ordered, not a rainbow. */
const RAMP: readonly (readonly [number, string])[] = [
  [0.0, '#3f4a3a'],
  [0.35, '#5d6a45'],
  [0.65, '#8a8560'],
  [0.85, '#a89b7e'],
  [1.0, '#cfc4ad'],
];

function rampColor(t: number, into: Color): Color {
  for (let i = 1; i < RAMP.length; i += 1) {
    const [t1, c1] = RAMP[i];
    const [t0, c0] = RAMP[i - 1];
    if (t <= t1) {
      const f = t1 === t0 ? 0 : (t - t0) / (t1 - t0);
      return into.set(c0).lerp(new Color(c1), f);
    }
  }
  return into.set(RAMP[RAMP.length - 1][1]);
}

/**
 * Builds a terrain mesh from a heightfield.
 *
 * World axes follow Three.js convention: x east, y up, z south. Heights are
 * metres, so the mesh is at true scale and the camera limits in spec §6.1 mean
 * what they say.
 */
export function buildTerrainMesh(field: Heightfield): Mesh {
  const { n, dxM, zM, minM, maxM } = field;
  const span = maxM - minM || 1;
  const positions = new Float32Array(n * n * 3);
  const colors = new Float32Array(n * n * 3);
  const colour = new Color();
  const halfM = ((n - 1) * dxM) / 2;

  for (let y = 0; y < n; y += 1) {
    for (let x = 0; x < n; x += 1) {
      const i = y * n + x;
      const z = zM[i];
      positions[i * 3] = x * dxM - halfM;
      positions[i * 3 + 1] = z;
      positions[i * 3 + 2] = y * dxM - halfM;
      rampColor((z - minM) / span, colour);
      colors[i * 3] = colour.r;
      colors[i * 3 + 1] = colour.g;
      colors[i * 3 + 2] = colour.b;
    }
  }

  // Two triangles per quad. Uint32 because 256^2 vertices already exceeds 16 bits.
  const indices = new Uint32Array((n - 1) * (n - 1) * 6);
  let k = 0;
  for (let y = 0; y < n - 1; y += 1) {
    for (let x = 0; x < n - 1; x += 1) {
      const a = y * n + x;
      const b = a + 1;
      const c = a + n;
      const d = c + 1;
      indices[k++] = a;
      indices[k++] = c;
      indices[k++] = b;
      indices[k++] = b;
      indices[k++] = c;
      indices[k++] = d;
    }
  }

  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(positions, 3));
  geometry.setAttribute('color', new BufferAttribute(colors, 3));
  geometry.setIndex(new BufferAttribute(indices, 1));
  geometry.computeVertexNormals();
  geometry.computeBoundingSphere();

  const material = new MeshStandardMaterial({ vertexColors: true, roughness: 0.95, metalness: 0 });
  const mesh = new Mesh(geometry, material);
  mesh.name = 'terrain';
  return mesh;
}

/** What {@link createScene} hands back to the host page. */
export interface SceneHandle {
  readonly renderer: WebGLRenderer;
  readonly scene: Scene;
  readonly camera: PerspectiveCamera;
  readonly backend: string;
  /** Vertices in the terrain mesh, for the HUD readout. */
  readonly vertexCount: number;
  resize(width: number, height: number): void;
  dispose(): void;
}

/** Creates the renderer, camera, lighting and terrain mesh. */
export function createScene(container: HTMLElement, field: Heightfield): SceneHandle {
  const renderer = new WebGLRenderer({ antialias: true, powerPreference: 'high-performance' });
  renderer.setPixelRatio(Math.min(globalThis.devicePixelRatio ?? 1, 2));
  container.appendChild(renderer.domElement);

  const scene = new Scene();
  scene.background = new Color('#9fb6c4');
  const extentM = field.n * field.dxM;
  scene.fog = new Fog('#9fb6c4', extentM * 0.8, extentM * 2.4);

  // A low sun, so the relief reads. The real diurnal cycle arrives with the water
  // and lighting work in M1 (spec §5.4, §6.5).
  const sun = new DirectionalLight('#fff3e0', 2.1);
  sun.position.set(-0.4, 0.8, -0.35).multiplyScalar(extentM);
  scene.add(sun);
  scene.add(new HemisphereLight('#bcd4e6', '#4a4536', 0.55));

  const mesh = buildTerrainMesh(field);
  scene.add(mesh);

  const camera = new PerspectiveCamera(50, 1, 1, extentM * 4);

  const handle: SceneHandle = {
    renderer,
    scene,
    camera,
    backend: 'webgl2',
    vertexCount: field.n * field.n,
    resize(width: number, height: number): void {
      renderer.setSize(width, height, false);
      camera.aspect = height === 0 ? 1 : width / height;
      camera.updateProjectionMatrix();
    },
    dispose(): void {
      mesh.geometry.dispose();
      (mesh.material as MeshStandardMaterial).dispose();
      renderer.dispose();
      renderer.domElement.remove();
    },
  };
  return handle;
}
